// Pure helpers & UI-only constants shared across the Dashboard sub-panels.

// Circularity-gap finder — the panel is now driven by the SHARED `circularityLoops`/`circularityScore`
// helpers (same math as the Resources ring), so the panel total EQUALS the ring %. Suggestions are a
// UI concern keyed by loop.
export const GAP_SUGGEST = {
  water: "Enable/raise a Water recovery loop (rainwater capture or greywater reuse) to close the gap.",
  energy: "Add PV area or recover exportable building heat (exhaust-air heat pump, run-around coil, or condenser heat) to offset grow-light + HVAC load.",
  nutrient: "Lock an Organic Valorization loop (compost / biodigester / BSF) to recover on-site nutrient.",
  carbon: "Select lower embodied-carbon systems or higher-yield crops to reach a net-positive balance.",
};

// PHASE 4 — annual water (L) & energy (kWh) demand of a paired instance from its crop allocation.
export function blockDemand(b, kb) {
  const cyc = (m) => (m && m.cycle ? Math.max(1, Math.min(26, 365 / m.cycle)) : 4);
  const sys = kb?.systems?.find((s) => s.id === b.systemId);
  const dbs = sys?.db_systems || b.dbSystems || [];
  let waterL = 0, energyKwh = 0;
  Object.entries(b.allocations || {}).forEach(([cid, pct]) => {
    if (!pct) return;
    const c = kb?.crops?.find((x) => x.id === cid);
    const key = c && dbs.find((lb) => c.systems[lb]);
    if (!key) return;
    const m = c.systems[key] || {};
    const kg = (m.yield || 0) * ((b.area || 0) * pct / 100) * cyc(m);
    waterL += (m.water_use || 0) * kg;
    energyKwh += (m.energy_use || 0) * kg;
  });
  return { waterL, energyKwh, has: waterL > 0 || energyKwh > 0 };
}
// Per-system performance metrics used by the Excel/CSV override template. Defaults are the
// average of the compatible crops for the block's DB system(s) + the system's electrical spec.
export const OVERRIDE_METRIC_ROWS = [
  { key: "yield", label: "Yield", unit: "kg/m2/cycle" },
  { key: "water_use", label: "Water Use", unit: "L/kg" },
  { key: "energy_use", label: "Energy Use", unit: "kWh/kg" },
  { key: "ghg", label: "Embodied Carbon", unit: "kg CO2e/kg" },
  { key: "hvac_load", label: "HVAC Load", unit: "kW/100m2" },
];
export function systemDefaultMetrics(block, kb) {
  const dbs = block.dbSystems || [];
  const cropsC = (kb?.crops || []).filter((c) => dbs.some((lb) => c.systems?.[lb]));
  const avg = (field) => {
    const vals = cropsC.map((c) => { const k = dbs.find((lb) => c.systems[lb]); return c.systems[k]?.[field]; }).filter((v) => v != null);
    return vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(3) : 0;
  };
  const sys = kb?.systems?.find((s) => s.id === block.systemId);
  return {
    yield: avg("yield"),
    water_use: avg("water_use"),
    energy_use: avg("energy_use"),
    ghg: avg("ghg"),
    hvac_load: +(sys?.specs?.electrical_kw_100m2 || 0),
  };
}
// PHASE 4 — professional 4-way taxonomy for the Resources ring. Order matters: renewables and
// energy outputs are matched before the water/organic keyword tests so "Solar Thermal (hot water)"
// and "digester → biogas" land correctly and "black soldier fly" is not read as blackwater.
export function resourceGroup(r) {
  const cat = r.category || "";
  const blob = `${r.tech || ""} ${r.output || ""} ${r.label || ""} ${r.source || ""}`.toLowerCase();
  if (cat === "Carbon (CO2) Recovery" || /\bco2\b|co₂|enrichment/.test(blob)) return "Carbon (CO2) Recovery";
  if (cat === "Renewable Energy" || /solar|photovolt|\bpv\b/.test(blob)) return "Renewable Energy";
  if (/biogas|electric/.test(blob)) return "Renewable Energy";
  if (/heat|hrv|hvac|exchang|pump|thermal/.test(blob)) return "Thermal Exchange";
  if (cat === "Water" || /greywater|rainwater|blackwater|\brain\b|potable|irrigation|treated water|struvite|membrane/.test(blob)) return "Water Recovery & Treatment";
  return "Organic Valorization";
}
